import axios from "axios";

/**
 * Axios call with automatic retry on failure (handles cold-start / transient errors).
 * Uses exponential backoff: delay doubles each attempt.
 * Default: 4 retries starting at 2s → 2,4,8,8 = ~22s total window
 * @param {Function} requestFn - A function that returns an axios promise, e.g. () => axios.get(url)
 * @param {Object} opts
 * @param {number} opts.retries - Max retry attempts (default 4)
 * @param {number} opts.delay - Initial delay between retries in ms (default 2000)
 * @param {number} opts.maxDelay - Max delay cap in ms (default 8000)
 * @param {boolean} opts.silent - If true, don't log retries (default false)
 * @returns {Promise} - The axios response
 */
export async function axiosWithRetry(requestFn, { retries = 4, delay = 2000, maxDelay = 8000, silent = false } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await requestFn();
    } catch (err) {
      lastError = err;
      // iter 370g31 — Bail out immediately on cancellation (e.g. React
      // Query aborts a stale query when invalidateQueries fires). Retrying
      // a cancelled signal just loops failing immediately and surfaces
      // CanceledError in the dev overlay.
      if (axios.isCancel?.(err) || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
        throw err;
      }
      // Don't retry on 4xx client errors (except 408 Request Timeout and 429 Too Many Requests)
      const status = err.response?.status;
      if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
        throw err;
      }
      if (attempt < retries) {
        const currentDelay = Math.min(delay * Math.pow(2, attempt), maxDelay);
        if (!silent) console.warn(`Request failed (attempt ${attempt + 1}/${retries + 1}), retrying in ${currentDelay}ms...`);
        await new Promise(r => setTimeout(r, currentDelay));
      }
    }
  }
  throw lastError;
}
